import { Component, Input, OnDestroy, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { FormGroup, AbstractControl, FormControl } from '@angular/forms';
import { ERRORS, Constants } from '../../models/constants';
import { MatDialog } from '@angular/material/dialog';
import { throwError, Observable, of } from 'rxjs';
import { distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { CoreCommonService } from '../../providers/services/common.service';
import { HideShowSchema, HideShowType, AgeCheckParam } from '../../models/interfaces';
import moment from 'moment';
import { CoreDataShareService } from '../../providers/services/data-share.service';

@Component({
  selector: 'app-dynamic-control',
  templateUrl: './dynamic-control.component.html'
})
export class DynamicControlComponent implements OnChanges, OnDestroy {
  @Input() fieldRow;
  @Input() form: FormGroup;
  @Input() scope: any;
  @Output() controlReady = new EventEmitter<boolean>();
  files = [];
  isApplicantListDropdownDisabled = true;
  private eventSubscriptionsList: SubSink;
  objectKeys = Object.keys;
  CONSTANTS = Constants;
  hideShowList = {};
  currentField: FormControl;
  thisScope = this;
  fieldRowCounter = 0;

  constructor(public dialog: MatDialog, private coreCommonService: CoreCommonService, private coreDataShareService: CoreDataShareService) {}

  /**
   * A callback method that is invoked if any data-bound properties change
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes?.fieldRow && changes.fieldRow?.currentValue) {
      try {
        if (this.fieldRow?.fields && this.fieldRow.fields[0].fieldType === Constants.controls.Image) {
          this.controlReady.emit(true);
        }
      } catch (error) {
        throwError(`${ERRORS.ERROR_OCCURED}: ${error}`);
      }
    }
    this.eventSubscriptionsList = new SubSink();
    this.handlePageLoad();
  }

  /**
   * Unsubscribe Observables and detach event handlers to avoid memory leaks
   */
  ngOnDestroy() {
    this.eventSubscriptionsList.unsubscribe();
  }

  /**
   * This method will handle the page load
   */
  private async handlePageLoad() {
    this.initVariables();
    await this.handleEvents(this.fieldRow, this.form);
  }

  /**
   * This method will initialize the variables
   */
  private initVariables() {
    this.fieldRowCounter = 0;
  }

  /**
   * This method will check if the field is valid or not
   * @param logicalName - logical name of field
   */
  isValid(logicalName: string) {
    return this.form.get(logicalName).untouched || this.form.get(logicalName).valid;
  }

  /**
   * This method will call the function from schema by name
   */
  callFunctionByName(fnName: string, ...params: any): Observable<any> {
    try {
      let scope;
      if (this[fnName]) {
        scope = this;
      } else if (this.scope[fnName]) {
        scope = this.scope;
      }
      const val = params ? scope[fnName](...params) : scope[fnName]();
      return of(val);
    } catch (error) {
      throwError(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    // return true;
  }

  // ------------------------------------- Supporting functions -------------------------------

  /**
   * This method will register any enable or disable events
   * @param field - field details
   * @param form - form data
   */
  private async registerEnableDisableEvents(fieldSchema: object, form: FormGroup) {
    try {
      if (!fieldSchema.hasOwnProperty('toggleDisableField') || fieldSchema['toggleDisableField']?.length === 0) {
        return false;
      }
      for (const eventSchema of fieldSchema['toggleDisableField'] as HideShowSchema[]) {
        this.hideShowList[fieldSchema['logicalName']] = false;
        this.checkAndSetEnableDisableEvents(eventSchema, form, fieldSchema);
      }
      return true;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will register and subscribe to any value change
   * @param enableDisableSchema - fields from schema
   * @param form - current form
   * @param fieldSchema - fields details
   */
  private checkAndSetEnableDisableEvents(enableDisableSchema: HideShowSchema, form: FormGroup, fieldSchema: object) {
    try {
      switch (enableDisableSchema.type) {
        case HideShowType.FORM_VALUE:
          const eventSourceField = form.get(enableDisableSchema.key);
          this.eventSubscriptionsList.sink = eventSourceField.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => {
            // TODO check if condition req or value need to be set in schema
            if (val === null || val === undefined || enableDisableSchema.value.includes(val)) {
              this.disableAndResetControl(form.get(fieldSchema['logicalName']));
              form.get(fieldSchema['logicalName']).reset();
              this.controlReady.emit(true);
            } else {
              this.enableControl(form.get(fieldSchema['logicalName']));
            }
          });
          break;

        case HideShowType.FUNCTION:
          this.handleDisableFunctionCall(form, fieldSchema, enableDisableSchema);
          break;
        default:
          this.enableControl(form.get(fieldSchema['logicalName']));
          break;
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This methid will handle all the disable/enable functions calls
   */
  private async handleDisableFunctionCall(form: FormGroup, fieldSchema: object, enableDisableSchema: HideShowSchema) {
    try {
      const params = this.coreCommonService.extractAndConstructParams(enableDisableSchema.params, form);
      const functionValue = this.callFunctionByName(enableDisableSchema.key, form, fieldSchema, enableDisableSchema, params);
      if (!functionValue) {
        throw new Error(`error: function_not_defined, ${enableDisableSchema.key}`);
      }
      this.eventSubscriptionsList.sink = functionValue.pipe(distinctUntilChanged()).subscribe(
        (val) => {
          if (val === enableDisableSchema.value) {
            this.disableAndResetControl(form.get(fieldSchema['logicalName']));
            this.controlReady.emit(true);
          } else {
            this.enableControl(form.get(fieldSchema['logicalName']));
          }
        },
        (err) => console.log('error :', err)
      );
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will register for any hide/show events
   */
  private async registerHideShowEvent(fieldSchema: object, form: FormGroup) {
    try {
      if (
        (!fieldSchema.hasOwnProperty('toggleHideShow') || fieldSchema['toggleHideShow'].length === 0) &&
        (!fieldSchema.hasOwnProperty('toggleDisableField') || fieldSchema['toggleDisableField']?.length === 0)
      ) {
        this.showAndEnableField(this.hideShowList, form, fieldSchema);
        return false;
      }

      for (const eventSchema of fieldSchema['toggleHideShow'] as HideShowSchema[]) {
        this.checkAndSetHideShowEvent(eventSchema, form, fieldSchema);
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will check and set for any hide/shoe events
   */
  private async checkAndSetHideShowEvent(hideShowSchema: HideShowSchema, form: FormGroup, fieldSchema: object) {
    try {
      switch (hideShowSchema.type) {
        case HideShowType.FORM_VALUE:
          const eventSourceField = form.get(hideShowSchema.key);
          this.eventSubscriptionsList.sink = eventSourceField.valueChanges.pipe(distinctUntilChanged()).subscribe((val) => {
            // TODO check if condition req or value need to be set in schema
            if (val === null || val === undefined || hideShowSchema.value.includes(val)) {
              this.hideAndDisableField(this.hideShowList, form, fieldSchema);
            } else {
              this.showAndEnableField(this.hideShowList, form, fieldSchema);
            }
          });
          break;

        case HideShowType.FUNCTION:
          this.handleHideFunctionCall(this.hideShowList, form, fieldSchema, hideShowSchema);
          break;
        case HideShowType.STATIC:
          this.hideShowList[fieldSchema['logicalName']] = hideShowSchema.value;
          // INFO: Added below code since for hidden fields, control is not loaded and hence base form is not notified.
          this.controlReady.emit(true);
          break;
        default:
          this.showAndEnableField(this.hideShowList, form, fieldSchema);
          break;
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This methid will handle all the hide functions calls
   */
  private async handleHideFunctionCall(list: object, form: FormGroup, fieldSchema: object, hideShowSchema: HideShowSchema) {
    try {
      const params = this.coreCommonService.extractAndConstructParams(hideShowSchema.params, form);
      const functionValue = this.callFunctionByName(hideShowSchema.key, form, fieldSchema, hideShowSchema, params);
      if (!functionValue) {
        throw new Error(`error: function_not_defined, ${hideShowSchema.key}`);
      }
      this.eventSubscriptionsList.sink = functionValue.pipe(distinctUntilChanged()).subscribe(
        (val) => {
          if (val === hideShowSchema.value) {
            this.hideAndDisableField(this.hideShowList, form, fieldSchema);
          } else {
            this.showAndEnableField(this.hideShowList, form, fieldSchema);
          }
        },
        (err) => console.log('error :', err)
      );
    } catch (error) {
      console.log(error);
    }
  }

  // INFO: add params with keys as `age` and `dob` in toggleHideShow schema
  /**
   * This method will hide fields if age is greater than specified data
   */
  hideIfAgeGreaterThan(form: FormGroup, fieldSchema: object, hideShowSchema: HideShowSchema, params: AgeCheckParam) {
    try {
      const age = moment().diff(params.dob, 'year');
      if (age > params.age) {
        this.hideAndDisableField(this.hideShowList, form, fieldSchema);
      } else {
        this.showAndEnableField(this.hideShowList, form, fieldSchema);
      }
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will hide and disable fields based on condition
   */
  private async hideAndDisableField(hideShowList: object, form: FormGroup, fieldSchema: object) {
    try {
      if (hideShowList.hasOwnProperty(fieldSchema['logicalName'])) {
        this.hideShowList[fieldSchema['logicalName']] = true;
        fieldSchema['hidden'] = this.hideShowList[fieldSchema['logicalName']];
        this.disableAndResetControl(form.get(fieldSchema['logicalName']));
        this.controlReady.emit(true);
      }
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will disable and reset control based on condtion
   * @param control - details
   */
  private disableAndResetControl(control: FormControl | AbstractControl) {
    try {
      control.disable();
      control.updateValueAndValidity();
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will enable control based on condition
   * @param control - details
   */
  private enableControl(control: FormControl | AbstractControl) {
    try {
      control.enable();
      control.updateValueAndValidity();
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will show and enable fields based on condition
   */
  private async showAndEnableField(hideShowList: object, form: FormGroup, fieldSchema: object) {
    try {
      if (hideShowList.hasOwnProperty(fieldSchema['logicalName'])) {
        this.hideShowList[fieldSchema['logicalName']] = false;
        fieldSchema['hidden'] = this.hideShowList[fieldSchema['logicalName']];

        if (fieldSchema['readOnly']) {
          this.disableAndResetControl(form.get(fieldSchema['logicalName']));
          form.get(fieldSchema['logicalName']).reset();
        } else {
          this.enableControl(form.get(fieldSchema['logicalName']));
        }
      }
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will handle events and registers them
   */
  private handleEvents(fields: object, form: FormGroup) {
    for (const field of fields['fields']) {
      this.hideShowList[field['logicalName']] = true;
      this.registerEvents(field, form);
    }
    return true;
  }

  /**
   * This method will register the events
   */
  private async registerEvents(field: object, form: FormGroup, parentFieldName?: string) {
    try {
      const enableDisable = this.registerEnableDisableEvents(field, form);
      const textValueChange = this.registerTextValueChange(field, form);
      const hideShow = this.registerHideShowEvent(field, form);
      const registerEvents = await Promise.all([enableDisable, textValueChange, hideShow]);
      return registerEvents;
    } catch (error) {
      console.log(`${ERRORS.EVENT_REGISTRATION_FAILED}: ${error}`);
    }
  }

  /**
   * This method will notify when all the controls are ready
   */
  notifyControlReady() {
    this.fieldRowCounter++;
    if (this.fieldRow?.fields.length === this.fieldRowCounter) {
      this.controlReady.emit(true);
    }
  }

  // TODO: Move this to appropriate field component. Optimize below code and try to make generic code if possible
  /**
   * This method will register for any text value changes
   * @param field - fields details
   * @param form - form data
   */
  private registerTextValueChange(field: object, form: FormGroup) {
    try {
      // This text value change should get applied to TEXT boxes only
      const applicableFieldTypes = [Constants.controls.Textbox, Constants.controls.Email];
      if (applicableFieldTypes.indexOf(field['fieldType']) === -1) {
        return;
      }
      const control: AbstractControl = form.get(field['logicalName']);
      if (control && field.hasOwnProperty('onChangeEvent') && field['onChangeEvent']?.length > 0) {
        this.eventSubscriptionsList[field['logicalName']] = control.valueChanges
          .pipe(distinctUntilChanged(), debounceTime(100))
          .subscribe((value) => {
            for (const eventSchema of field['onChangeEvent']) {
              if (form.get(field['logicalName']).valid) {
                this.callFunctionByName(eventSchema['functionName'], field, value, control);
              }
            }
          });
      }
    } catch (error) {
      throwError(`${ERRORS.TEXT_VAL_CHANGE_EVENT_REG_FAILED}: ${error}`);
    }
  }

  /**
   * This method will handle all the on click events
   * @param field - fields details
   */
  handleOnClickEvent(field: object) {
    try {
      const onClickEvent = field['onClickEvent'];
      for (const event of onClickEvent) {
        const params: Array<any> = event.params || [];
        this.callFunctionByName(event.functionName, event.url, params, this.coreDataShareService.currentFormGroup);
      }
    } catch (error) {
      console.log(`${ERRORS.ONCLICK_EVENT_FAILED}: ${error}`);
    }
  }

  /**
   * The trackBy function takes the index and the current item as arguments
   * and needs to return the unique identifier for this item
   * @param index - item index
   */
  trackByFn(index: number, item): number {
    if (!item) {
      return null;
    }
    return index;
  }
}
